import { yupResolver } from '@hookform/resolvers/yup'
import { Box, OutlinedInput, Typography } from '@mui/material'
import { Controller, useForm } from 'react-hook-form'
import * as yup from 'yup'

import { ErrorText, FormLabel, GenericDialog, MButton } from 'components'
import { color } from 'config/color'
import { useAuth } from 'context'
import { useSnackbar } from 'notistack'
import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { isAuthorized } from 'utils'

interface IEditEmailDialog {
  open: boolean
  onClose: () => void
  email: string
}

interface IFormData {
  email: any
  emailConfirm: any
}
export const EditEmailDialog = ({
  open,
  onClose,
  email
}: IEditEmailDialog): JSX.Element => {
  const { enqueueSnackbar } = useSnackbar()
  const navigate = useNavigate()
  const { account, updateEmail, handleLogOut } = useAuth()
  const [isLoading, setIsLoading] = useState<boolean>(false)

  const schema = yup
    .object({
      email: yup.string().email().required('Email is required'),
      emailConfirm: yup
        .string()
        .oneOf([yup.ref('email'), null], 'Email must match')
        .notOneOf([email], 'New email must be different from the old email')
    })
    .required()

  const {
    handleSubmit,
    control,
    formState: { errors, isValid }
  } = useForm<IFormData>({
    mode: 'all',
    defaultValues: {
      email: '',
      emailConfirm: ''
    },
    resolver: yupResolver(schema)
  }) as {
    handleSubmit: any
    control: any
    formState: { errors: any, isValid: any }
  }

  const handleUpdateEmail = async (data: any): Promise<void> => {
    setIsLoading(true)
    try {
      const response: any = await updateEmail(
        email,
        data.email,
        account?.accountId
      )
      if (response.success) {
        enqueueSnackbar(
          `Email address has been changed to ${data.emailConfirm}`,
          { variant: 'success', autoHideDuration: 6000 }
        )
      } else if (response?.error) {
        enqueueSnackbar(response?.error, {
          variant: 'error',
          autoHideDuration: 4000
        })
      }
      onClose()
      navigate(`/account-details?email=${response?.data?.email ?? data.email}`)
    } catch (e: any) {
      if (!isAuthorized(e)) await handleLogOut()
      enqueueSnackbar(e.response?.data?.error || e?.message, {
        variant: 'error',
        autoHideDuration: 3000
      })
      onClose()
    }
    setIsLoading(false)
  }

  const onSubmit = async (data: any): Promise<void> => {
    await handleUpdateEmail(data)
  }

  return (
    <Box>
      <GenericDialog open={open} onClose={onClose}>
        <form method="post" onSubmit={handleSubmit(onSubmit)}>
          <Box display="flex" flexDirection="column" gap={2}>
            <Box display="flex" flexDirection="column">
              <Typography
                color={color.grey[900]}
                fontWeight={500}
                fontSize={24}
              >
                Update email address
              </Typography>

              <Typography
                color={color.grey[700]}
                fontWeight={400}
                fontSize={16}
              >
                Enter the new email you want to use.
              </Typography>
            </Box>

            <Box display="flex" flexDirection="column" gap={1}>
              <Controller
                name="email"
                control={control}
                render={({ field: { onChange, value, name } }): JSX.Element => (
                  <>
                    <FormLabel textcolor={color.grey[700]}>
                      Email address
                    </FormLabel>
                    <OutlinedInput
                      name={name}
                      placeholder="Enter email address"
                      onChange={onChange}
                      value={value}
                      type="text"
                      fullWidth
                    />
                  </>
                )}
              />

              <Controller
                name="emailConfirm"
                control={control}
                render={({ field: { onChange, value, name } }): JSX.Element => (
                  <>
                    <FormLabel textcolor={color.grey[700]}>
                      Confirm email address
                    </FormLabel>
                    <OutlinedInput
                      name={name}
                      placeholder="Enter email address"
                      onChange={onChange}
                      value={value}
                      type="text"
                      fullWidth
                    />
                    {errors.emailConfirm && (
                      <ErrorText>{errors.emailConfirm.message}</ErrorText>
                    )}
                  </>
                )}
              />
            </Box>

            <Box display="flex" flexDirection="column" gap={1}>
              <MButton
                loading={isLoading}
                type="submit"
                variant="contained"
                rounded="true"
                texttransform="none"
                disabled={!isValid || isLoading}
              >
                Update Email
              </MButton>

              <MButton
                variant="outlined"
                type="button"
                rounded="true"
                texttransform="none"
                onClick={onClose}
              >
                Go Back
              </MButton>
            </Box>
          </Box>
        </form>
      </GenericDialog>
    </Box>
  )
}
